<template>
    <div class="main-content">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium text-dark">{{ alert.message }}</div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"><router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Ponto de Venda (PDV)</li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0 mb-4">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold text-dark">Caixa Aberto</h4>
                    <span class="text-muted fs-7">Adicione produtos ao carrinho para a venda</span>
                </div>
                <div class="d-flex gap-2 align-items-center">
                    <button class="btn btn-pmg-light" @click="clearCart" :disabled="cart.length === 0">
                        <i class="bi bi-arrow-counterclockwise me-1"></i> Limpar Carrinho
                    </button>
                    <button class="btn btn-pmg-primary" :disabled="cart.length === 0" @click="openCheckoutModal">
                        <i class="bi bi-wallet2 me-1"></i> Finalizar Venda
                    </button>
                </div>
            </div>

            <div class="card card-body mb-4 border-0 bg-light rounded-3">
                <div class="row g-3 align-items-end">
                    <div class="col-md-7 position-relative">
                        <label class="form-label fw-bold text-dark small">Buscar Produto</label>
                        <div class="input-group">
                            <span class="input-group-text bg-white border-end-0"><i class="bi bi-search text-muted"></i></span>
                            <input 
                                v-model="searchQuery" 
                                @input="debouncedSearch"
                                type="text" 
                                class="form-control form-control-pmg border-start-0 ps-0"
                                placeholder="Digite o nome do produto ou SKU..." 
                                autocomplete="off"
                            >
                        </div>

                        <ul v-if="searchResults.length" class="list-group position-absolute w-100 search-dropdown shadow-lg rounded-3 border-0 mt-1">
                            <li
                                v-for="product in searchResults"
                                :key="product.id"
                                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center border-0 border-bottom py-3"
                                @click="selectProduct(product)"
                            >
                                <div>
                                    <div class="fw-bold text-dark">{{ product.name }}</div>
                                    <div class="small text-muted">SKU: {{ product.sku }} | Estoque: {{ product.stock }}</div>
                                </div>
                                <span class="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-6">
                                    {{ formatCurrency(product.price) }}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div class="col-md-2">
                        <label class="form-label fw-bold text-dark small">Quantidade</label>
                        <input 
                            v-model.number="quantity" 
                            type="number" 
                            class="form-control form-control-pmg text-center"
                            min="1"
                        >
                    </div>

                    <div class="col-md-3 d-flex">
                        <button class="btn btn-dark w-100 btn-pmg-dark fw-bold" @click="addToCart" :disabled="!selectedProduct || quantity < 1">
                            <i class="bi bi-cart-plus me-1"></i> Adicionar
                        </button>
                    </div>
                </div>
            </div>

            <div class="table-container mt-2">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 40%">Produto</th>
                            <th style="width: 15%" class="text-center">Qtd</th>
                            <th style="width: 15%" class="text-end">Preço Unitário</th>
                            <th style="width: 20%" class="text-end">Subtotal</th>
                            <th style="width: 10%" class="text-end">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in cart" :key="index">
                            <td>
                                <div class="fw-bold text-dark">{{ item.product.name }}</div>
                                <div class="small text-muted">{{ item.product.sku }}</div>
                            </td>
                            <td class="text-center fw-medium text-dark">
                                {{ item.quantity }} {{ item.product.unit_of_measure || 'UN' }}
                            </td>
                            <td class="text-end text-muted small">{{ formatCurrency(item.product.price) }}</td>
                            <td class="text-end fw-bold text-dark">{{ formatCurrency(item.product.price * item.quantity) }}</td>
                            <td class="text-end">
                                <button class="btn btn-pmg-icon text-danger" @click="removeFromCart(index, item.product.name)">
                                    <i class="bi bi-trash3"></i>
                                </button>
                            </td>
                        </tr>
                        
                        <tr v-if="cart.length === 0">
                            <td colspan="5" class="text-center py-5">
                                <div class="text-muted mb-2"><i class="bi bi-cart-x fs-1"></i></div>
                                <div class="fw-medium">Nenhum item no carrinho</div>
                                <div class="small">Pesquise e adicione produtos acima para iniciar a venda.</div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot v-if="cart.length > 0">
                        <tr>
                            <td colspan="3" class="text-end fw-bold text-muted pt-4">Total da Venda:</td>
                            <td colspan="2" class="text-end pt-4"><h3 class="mb-0 fw-bold text-primary">{{ formatCurrency(cartTotal) }}</h3></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>

        <div class="modal fade" id="checkoutModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-bottom-0 pb-0 pt-4 px-4">
                        <h5 class="fw-bold text-dark"><i class="bi bi-wallet2 me-2 text-primary"></i> Finalizar Venda</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body p-4">
                        <div class="row g-4">
                            
                            <div class="col-md-6 border-end pe-md-4">
                                <h6 class="fw-bold text-secondary mb-3">1. Identificação do Cliente</h6>
                                
                                <div v-if="!selectedClient && !isCreatingClient" class="position-relative mb-3">
                                    <div class="input-group">
                                        <span class="input-group-text bg-light border-end-0"><i class="bi bi-person-bounding-box text-muted"></i></span>
                                        <input 
                                            v-model="clientSearch" 
                                            @input="debouncedClientSearch"
                                            type="text" 
                                            class="form-control form-control-pmg border-start-0 ps-0 bg-light"
                                            placeholder="Buscar cliente (nome, CPF, CNPJ)..." 
                                        >
                                    </div>

                                    <ul v-if="clientResults.length > 0" class="list-group position-absolute w-100 shadow-sm mt-1" style="z-index: 1060; max-height: 200px; overflow-y: auto;">
                                        <li 
                                            v-for="client in clientResults" :key="client.id"
                                            class="list-group-item list-group-item-action py-2 cursor-pointer"
                                            @click="selectClient(client)"
                                        >
                                            <div class="fw-bold">{{ client.name }}</div>
                                            <div class="small text-muted">{{ client.document }}</div>
                                        </li>
                                    </ul>
                                    <div v-else-if="clientSearch.length >= 3 && clientResults.length === 0" class="position-absolute w-100 shadow-sm mt-1 bg-white border rounded p-3 text-center" style="z-index: 1060;">
                                        <p class="small text-muted mb-2">Nenhum cliente encontrado.</p>
                                        <button class="btn btn-sm btn-pmg-primary w-100" @click="isCreatingClient = true">
                                            <i class="bi bi-plus-circle me-1"></i> Cadastrar Novo Cliente
                                        </button>
                                    </div>
                                </div>

                                <div v-if="selectedClient" class="bg-primary-subtle p-3 rounded-3 border border-primary border-opacity-25 d-flex justify-content-between align-items-center">
                                    <div>
                                        <div class="fw-bold text-primary-emphasis">{{ selectedClient.name }}</div>
                                        <div class="small text-primary">{{ selectedClient.document || 'Sem documento cadastrado' }}</div>
                                    </div>
                                    <button class="btn btn-sm btn-outline-primary border-0" @click="clearClient">
                                        <i class="bi bi-x-lg"></i>
                                    </button>
                                </div>

                                <div v-if="isCreatingClient" class="bg-light p-3 rounded-3 border">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <span class="fw-bold small text-dark">Novo Cadastro</span>
                                        <button class="btn btn-sm text-danger p-0" @click="isCreatingClient = false">
                                            <i class="bi bi-x-circle"></i> Cancelar
                                        </button>
                                    </div>
                                    <div class="row g-2">
                                        <div class="col-12">
                                            <input v-model="clientForm.name" type="text" class="form-control form-control-sm" placeholder="Nome Completo *" required>
                                        </div>
                                        <div class="col-6">
                                            <input v-model="clientForm.document" type="text" class="form-control form-control-sm" placeholder="CPF/CNPJ *">
                                        </div>
                                        <div class="col-6">
                                            <input v-model="clientForm.phone" type="text" class="form-control form-control-sm" placeholder="Telefone">
                                        </div>
                                        <div class="col-12">
                                            <input v-model="clientForm.email" type="email" class="form-control form-control-sm" placeholder="E-mail (Opcional)">
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6 ps-md-2">
                                <h6 class="fw-bold text-secondary mb-3">2. Pagamento</h6>
                                <div class="mb-3">
                                    <label class="form-label small fw-bold mb-1">Forma de Pagamento</label>
                                    <select v-model="payment.method" class="form-select form-select-sm form-control-pmg">
                                        <option value="" disabled>Selecione...</option>
                                        <option value="pix">Pix</option>
                                        <option value="credit_card">Cartão de Crédito</option>
                                        <option value="debit_card">Cartão de Débito</option>
                                        <option value="boleto">Boleto Bancário</option>
                                        <option value="cash">Dinheiro</option>
                                    </select>
                                </div>

                                <div v-if="payment.method === 'credit_card'" class="mb-3">
                                    <label class="form-label small fw-bold mb-1">Parcelamento</label>
                                    <select v-model="payment.installments" class="form-select form-select-sm form-control-pmg">
                                        <option v-for="n in 12" :key="n" :value="n">
                                            {{ n }}x de {{ formatCurrency(finalTotal / n) }} {{ n === 1 ? '(À vista)' : 'sem juros' }}
                                        </option>
                                    </select>
                                </div>
                              <div class="mb-3">
                                <label class="form-label small fw-bold mb-1">Desconto</label>
                                <div class="input-group input-group-sm">
                                    <span class="input-group-text bg-white">R$</span>
                                    <input 
                                        v-model="formattedDiscount" 
                                        type="text" 
                                        class="form-control form-control-pmg text-end"
                                        placeholder="0,00"
                                    >
                                </div>
                               </div>
                                <div class="bg-dark text-white p-3 rounded-3 mt-4 shadow-sm">
                                    <div class="d-flex justify-content-between small text-white-50 mb-1">
                                        <span>Subtotal:</span>
                                        <span>{{ formatCurrency(cartTotal) }}</span>
                                    </div>
                                    <div v-if="payment.discount > 0" class="d-flex justify-content-between small text-danger mb-2">
                                        <span>Desconto:</span>
                                        <span>- {{ formatCurrency(payment.discount) }}</span>
                                    </div>
                                    <hr class="border-secondary my-2">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <span class="fw-bold">Total a Pagar:</span>
                                        <span class="fs-4 fw-bold text-success">{{ formatCurrency(finalTotal) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer border-top-0 pt-0 pb-4 px-4 d-flex justify-content-between">
                        <button type="button" class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Voltar</button>
                        <button class="btn btn-pmg-primary px-5 fw-bold" @click="submitSale" :disabled="!isCheckoutValid || isProcessing">
                            <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
                            <i v-else class="bi bi-check2-circle me-1"></i> Confirmar Venda
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';
import debounce from 'lodash/debounce';

export default {
    name: 'PDVComponent',
    data() {
        return {
            // Arrays Gerais e Estados
            alerts: [],
            loading: false,

            // Variáveis do Carrinho e Pesquisa de Produtos
            searchQuery: '',
            searchResults: [],
            selectedProduct: null,
            quantity: 1,
            cart: [],

            // Variáveis do Modal de Checkout
            checkoutModal: null,
            isProcessing: false,

            // Variáveis do Cliente
            clientSearch: '',
            clientResults: [],
            selectedClient: null,
            isCreatingClient: false,
            clientForm: { 
                name: '', 
                document: '', 
                email: '', 
                phone: '' 
            },

            // Variáveis de Pagamento
            payment: {
                method: '',
                installments: 1,
                discount: 0
            }
        }
    },
    computed: {
        cartTotal() {
            return this.cart.reduce((total, item) => {
                return total + (item.product.price * item.quantity);
            }, 0);
        },
        finalTotal() {
            const total = this.cartTotal - (parseFloat(this.payment.discount) || 0);
            return Math.max(0, total); // Evita valores negativos se o desconto for maior que o total
        },
        isCheckoutValid() {
            // A venda só pode ser finalizada se houver uma forma de pagamento escolhida
            if (!this.payment.method) return false;
            
            // Se estiver cadastrando um cliente, valida se os campos mínimos foram preenchidos
            if (this.isCreatingClient) {
                return this.clientForm.name.length > 2 && this.clientForm.document.length >= 11;
            }
            
            return true;
        },
        formattedDiscount: {
            get() {
                // Pega o valor real numérico e exibe formatado (ex: 15.50 vira "15,50")
                return (parseFloat(this.payment.discount) || 0).toLocaleString('pt-BR', { 
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },
            set(value) {
                // Quando o usuário digita, remove tudo que não for número
                let numericValue = value.replace(/\D/g, '');
                // Converte para decimal (ex: digitou "1550", divide por 100 = 15.50)
                this.payment.discount = numericValue ? parseFloat(numericValue) / 100 : 0;
            }
        }
    },
    created() {
        // Inicializa os debounces (lodash) para pesquisas, evitando sobrecarregar a API
        this.debouncedSearch = debounce(this.fetchProducts, 400);
        this.debouncedClientSearch = debounce(this.fetchClients, 500);
    },
    mounted() {
        // Instancia o modal do Bootstrap nativamente
        const modalElement = document.getElementById('checkoutModal');
        if (modalElement) {
            this.checkoutModal = new window.bootstrap.Modal(modalElement);
        }
    },
    methods: {
        async fetchProducts() {
            if (this.searchQuery.trim().length < 2) {
                this.searchResults = [];
                return;
            }
            try {
                const res = await api.get('/products', { 
                    params: { search: this.searchQuery, limit: 10 } 
                });
                this.searchResults = res.data.data || res.data;
            } catch (error) {
                this.showAlert('Erro ao buscar produtos. Verifique a conexão.', 'danger');
            }
        },

        selectProduct(product) {
            this.selectedProduct = product;
            this.searchQuery = product.name; // Trava o nome do produto no input visualmente
            this.searchResults = []; // Fecha o dropdown
        },

        addToCart() {
            if (!this.selectedProduct || this.quantity < 1) return;

            // Bloqueia adição se exceder o estoque disponível
            if (this.quantity > this.selectedProduct.stock) {
                this.showAlert(`Estoque insuficiente. Apenas ${this.selectedProduct.stock} disponíveis.`, 'warning');
                return;
            }

            const existingItemIndex = this.cart.findIndex(
                item => item.product.id === this.selectedProduct.id
            );

            if (existingItemIndex !== -1) {
                this.cart[existingItemIndex].quantity += this.quantity;
            } else {
                this.cart.push({
                    product: this.selectedProduct,
                    quantity: this.quantity
                });
            }

            this.showAlert(`${this.quantity}x ${this.selectedProduct.name} adicionado(s)!`, 'success');

            // Limpa o formulário de inserção para o próximo bip/busca
            this.selectedProduct = null;
            this.searchQuery = '';
            this.quantity = 1;
        },

        removeFromCart(index, productName) {
            this.cart.splice(index, 1);
            this.showAlert(`Item "${productName}" removido do carrinho.`);
        },

        clearCart() {
            this.cart = [];
            this.showAlert('O carrinho foi esvaziado.', 'warning');
        },

        // ==========================================
        // LÓGICA DE CHECKOUT E CLIENTES
        // ==========================================

        openCheckoutModal() {
            if (this.cart.length === 0) return;
            
            // Sempre que o modal abrir, garante que os dados de pagamento estão resetados
            this.payment = { method: '', installments: 1, discount: 0 };
            this.clearClient();
            this.isCreatingClient = false;
            
            this.checkoutModal.show();
        },

        async fetchClients() {
            if (this.clientSearch.trim().length < 3) {
                this.clientResults = [];
                return;
            }
            try {
                const res = await api.get('/clients', { 
                    params: { search: this.clientSearch, limit: 5 } 
                });
                this.clientResults = res.data.data || res.data;
            } catch (error) {
                console.error("Erro ao buscar clientes.", error);
            }
        },

        selectClient(client) {
            this.selectedClient = client;
            this.clientSearch = '';
            this.clientResults = [];
        },

        clearClient() {
            this.selectedClient = null;
            this.clientSearch = '';
            this.clientForm = { name: '', document: '', email: '', phone: '' };
        },

        async submitSale() {
            this.isProcessing = true;
            try {
                let finalClientId = this.selectedClient ? this.selectedClient.id : null;

                // Fluxo 1: Se o usuário está criando o cliente na hora
                if (this.isCreatingClient) {
                    const clientRes = await api.post('/clients', this.clientForm);
                    finalClientId = clientRes.data.id || clientRes.data.data.id; 
                }

                // Prepara os dados conforme o back-end (Laravel) espera receber
                const payload = {
                    client_id: finalClientId,
                    payment_method: this.payment.method,
                    installments: this.payment.installments,
                    discount: this.payment.discount,
                    items: this.cart.map(item => ({
                        product_id: item.product.id,
                        quantity: item.quantity
                    }))
                };

                // Envia a requisição de venda
                await api.post('/sales', payload);

                this.showAlert('Venda concluída com sucesso!', 'success');
                
                // Limpa o estado da tela para o próximo cliente
                this.cart = [];
                this.checkoutModal.hide();
                
            } catch (error) {
                const errorMsg = error.response?.data?.message || 'Erro ao processar a venda.';
                this.showAlert(errorMsg, 'danger');
            } finally {
                this.isProcessing = false;
            }
        },

        // ==========================================
        // HELPERS GERAIS (Alertas e Formatação)
        // ==========================================

        formatCurrency(value) {
            return (parseFloat(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        },

        showAlert(m, t = 'success') {
            const id = Date.now();
            let icon = 'bi-info-circle-fill';
            
            if (t === 'success') icon = 'bi-check-circle-fill';
            if (t === 'danger') icon = 'bi-exclamation-circle-fill';
            if (t === 'warning') icon = 'bi-exclamation-triangle-fill';
            
            this.alerts.push({ id, message: m, type: t, icon });
            
            // Remove o alerta da tela após 4 segundos
            setTimeout(() => this.removeAlert(id), 8000);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        }
    }
}
</script>

<style scoped>
/* ========================================== */
/* ESTILIZAÇÃO E PADRÃO PMG                   */
/* ========================================== */

.main-content { background-color: #f8fafc; min-height: 100vh; padding: 2rem; }
.card { border-radius: 16px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02); }

/* Botões Customizados */
.btn-pmg-primary { background: #0d6efd; color: #fff; border-radius: 10px; font-weight: 600; padding: 0.5rem 1rem; }
.btn-pmg-primary:hover { background: #0b5ed7; color: #fff; }
.btn-pmg-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-pmg-light { background: #f8fafc; color: #475569; border: 1px solid #e2e8f0; border-radius: 10px; font-weight: 600; }
.btn-pmg-light:hover { background: #e2e8f0; }

.btn-pmg-dark { background: #1e293b; color: #fff; border-radius: 10px; }
.btn-pmg-dark:disabled { opacity: 0.6; }

.btn-pmg-icon { background: transparent; border: none; font-size: 1.1rem; color: #64748b; transition: all 0.2s; }
.btn-pmg-icon:hover { color: #0f172a; transform: scale(1.1); }

/* Inputs Customizados */
.form-control-pmg { border-radius: 10px; border: 1px solid #e2e8f0; padding: 0.6rem 1rem; box-shadow: none; }
.form-control-pmg:focus { border-color: #94a3b8; box-shadow: 0 0 0 0.25rem rgba(148, 163, 184, 0.1); }
.cursor-pointer { cursor: pointer; }

/* Tabela Customizada */
.table-pmg thead th { 
    background: #f8fafc; 
    color: #64748b; 
    font-size: 0.7rem; 
    text-transform: uppercase; 
    letter-spacing: 1px; 
    padding: 1.2rem; 
    border-bottom: 1px solid #e2e8f0; 
}
.table-pmg tbody td { 
    padding: 1.2rem; 
    border-bottom: 1px solid #f1f5f9; 
}

/* Dropdown de Busca Customizado */
.search-dropdown {
    z-index: 1050;
    max-height: 300px;
    overflow-y: auto;
    scrollbar-width: thin;
}
.search-dropdown .list-group-item {
    cursor: pointer;
    transition: background-color 0.2s ease;
}
.search-dropdown .list-group-item:hover {
    background-color: #f1f5f9;
}

/* Animações de Toast */
.slide-fade-enter-active { transition: all 0.3s ease-out; }
.slide-fade-leave-active { transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1); }
.slide-fade-enter-from, .slide-fade-leave-to { transform: translateX(20px); opacity: 0; }
</style>